/**
 * "Import from board": pick one of the current board's groups to capture into the
 * editor. Lists the groups with their colours, offers to include updates as
 * plain text, and hands the choice back; the editor does the reading.
 */
import { Loader2 } from 'lucide-react';
import { Button, Checkbox, Modal, ModalContent, ModalFooter } from 'monday-ui-react-core';
import React, { useEffect, useState } from 'react';
import { fetchGroups } from '../../lib/captureGroup';

export default function GroupPicker({ open, api, boardId, capturing, onCapture, onClose }) {
  const [groups, setGroups] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [includeUpdates, setIncludeUpdates] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setGroups(null);
    setError(null);
    fetchGroups(api, boardId)
      .then((list) => alive && setGroups(list))
      .catch((e) => alive && setError((e && e.message) || 'request failed'));
    return () => {
      alive = false;
    };
  }, [open, api, boardId]);

  return (
    <Modal show={open} onClose={onClose} title="Import a group from this board">
      <ModalContent>
        <p className="mb-3 text-sm text-secondary">
          The group's items, their subitems and values become this template's rows. Nothing is saved until you
          click Save template.
        </p>
        {groups === null && !error && (
          <div className="inline-flex items-center gap-2 text-sm text-secondary" role="status">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Reading the board's groups
          </div>
        )}
        {error && (
          <div className="text-sm text-primary" role="alert">
            Could not read the board's groups. monday said: “{error}”.
          </div>
        )}
        {groups && !groups.length && <div className="text-sm text-secondary">This board has no groups.</div>}
        {groups && groups.length > 0 && (
          <div className="flex flex-col gap-1 max-h-72 overflow-y-auto" role="radiogroup" aria-label="Groups">
            {groups.map((g) => (
              <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-hover">
                <input type="radio" name="spt-group-pick" checked={groupId === g.id} onChange={() => setGroupId(g.id)} />
                <span className="w-3 h-3 rounded-full flex-none" style={{ backgroundColor: g.color }} aria-hidden="true" />
                <span className="text-primary">{g.title}</span>
              </label>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Checkbox
            label="Include updates (text and mentions, no files)"
            checked={includeUpdates}
            onChange={(e) => setIncludeUpdates(Boolean(e.target.checked))}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex justify-end gap-3">
          <Button kind={Button.kinds.SECONDARY} onClick={onClose} disabled={capturing}>
            Cancel
          </Button>
          <Button
            disabled={!groupId || capturing}
            loading={capturing}
            onClick={() => onCapture({ groupId, includeUpdates })}
          >
            Import
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
